import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  Alert,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { getCollectionsSelectedLocation } from '../../navigation/components/CollectionsLocationToggle';
import { collectionCandidatesQueryKey } from '../../clients/hooks/useCollectionClients';
import { statementsQueryKey } from '../../statements/hooks/useStatements';
import { useInvoicePdfUpload } from '../../clients/hooks/useInvoicePdfUpload';
import { formatDollar, formatOptionalDate } from '../../clients/utils/format';
import { MissingInvoice } from '../model';
import { missingInvoicesQueryKey, useMissingInvoices } from '../hooks/useMissingInvoices';
import UploadedInvoiceDialog, { UploadedNotice } from '../components/UploadedInvoiceDialog';

const NO_DEPARTMENT = 'No department';
const EMPTY_QUEUE_COPY = 'No missing invoices — every open invoice at this location has a PDF.';

const dropRemoved = (rows: MissingInvoice[], removed: Set<string>): MissingInvoice[] =>
  removed.size === 0 ? rows : rows.filter((row) => !removed.has(row.apply_to));

const writeMissingInvoicesCache = (queryClient: QueryClient, location: string, rows: MissingInvoice[]) => {
  queryClient.setQueryData<AxiosResponse<MissingInvoice[]>>(missingInvoicesQueryKey(location), (old) => {
    if (!old) return old;
    return { ...old, data: rows };
  });
};

const departmentLabel = (department: string | null): string => department || NO_DEPARTMENT;

const filterRows = (
  rows: MissingInvoice[],
  company: string,
  billCode: string,
  invoiceNumber: string,
  department: string,
): MissingInvoice[] => {
  const qCompany = company.trim().toLowerCase();
  const qBillCode = billCode.trim().toLowerCase();
  const qInvoice = invoiceNumber.trim().toLowerCase();

  return rows.filter((row) => {
    if (qCompany && !(row.company ?? '').toLowerCase().includes(qCompany)) return false;
    if (qBillCode && !(row.bill_code ?? '').toLowerCase().includes(qBillCode)) return false;
    if (qInvoice && !row.apply_to.toLowerCase().includes(qInvoice)) return false;
    if (department !== 'all' && departmentLabel(row.department) !== department) return false;
    return true;
  });
};

const buildColumns = (triggerUpload: (applyTo: string, acctNo: string) => void, uploadingApplyTo: string | null) => [
  { name: 'Client', selector: (row: MissingInvoice) => row.company, sortable: true, grow: 2 },
  { name: 'Bill Code', selector: (row: MissingInvoice) => row.bill_code, sortable: true, width: '110px' },
  { name: 'Invoice #', selector: (row: MissingInvoice) => row.apply_to, sortable: true, width: '110px' },
  {
    name: 'Invoice Date',
    // Sort on the raw ISO value. A formatted MM/DD/YYYY string sorts lexicographically and
    // scrambles any range crossing a year boundary. String selector so defaultSortField matches.
    selector: 'invoice_date',
    cell: (row: MissingInvoice) => <Typography variant="body2">{formatOptionalDate(row.invoice_date)}</Typography>,
    sortable: true,
    width: '130px',
  },
  // {
  //   name: 'Due Date',
  //   selector: 'due_date',
  //   cell: (row: MissingInvoice) => <Typography variant="body2">{formatOptionalDate(row.due_date)}</Typography>,
  //   sortable: true,
  //   width: '130px',
  // },
  {
    name: 'Amount',
    selector: (row: MissingInvoice) => row.amount,
    cell: (row: MissingInvoice) => (
      <Typography
        variant="body2"
        sx={{
          color: row.amount > 0 ? 'error.main' : row.amount < 0 ? 'info.main' : 'text.primary',
        }}
      >
        {formatDollar(row.amount)}
      </Typography>
    ),
    sortable: true,
    right: true,
    width: '130px',
  },
  {
    name: 'Department',
    selector: (row: MissingInvoice) => departmentLabel(row.department),
    sortable: true,
    grow: 1,
  },
  {
    name: '',
    center: true,
    width: '200px',
    cell: (row: MissingInvoice) => (
      <Button
        size="small"
        variant="outlined"
        startIcon={<UploadFileIcon />}
        disabled={uploadingApplyTo === row.apply_to}
        onClick={() => triggerUpload(row.apply_to, row.acct_no)}
      >
        {uploadingApplyTo === row.apply_to ? 'Uploading…' : 'Upload Invoice'}
      </Button>
    ),
  },
];

const MissingInvoicesListPage: React.FC = () => {
  // Read once at mount. The location toggle writes sessionStorage and reloads the window, so
  // this cannot drift — and it is the ONLY location value the page uses: fetch, query key,
  // upload payload, and sibling invalidations. The same apply_to can exist in both locations.
  const [location] = useState(() => getCollectionsSelectedLocation());
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useMissingInvoices(location);
  const removedApplyTosRef = useRef(new Set<string>());

  const [invoices, setInvoices] = useState<MissingInvoice[]>([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [billCodeFilter, setBillCodeFilter] = useState('');
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [uploadedNotice, setUploadedNotice] = useState<UploadedNotice | null>(null);

  useEffect(() => {
    if (!data?.data) return;

    const incoming = data.data;
    const visible = dropRemoved(incoming, removedApplyTosRef.current);
    setInvoices(visible);

    // An in-flight GET started by the file-picker focus can overwrite setQueryData from dismiss.
    // Re-patch so remount does not hydrate the filled row from a stale cache.
    if (visible.length !== incoming.length) {
      writeMissingInvoicesCache(queryClient, location, visible);
    }
  }, [data, location, queryClient]);

  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo } = useInvoicePdfUpload({
    location,
    onUploaded: ({ applyTo, acctNo, pdf, pickedFilename }) => {
      const row = invoices.find((item) => item.acct_no === acctNo && item.apply_to === applyTo);
      setUploadedNotice({
        applyTo,
        company: row?.company ?? null,
        pickedFilename,
        storedFilename: pdf.filename,
      });
    },
  });

  const handleNoticeDismiss = () => {
    const notice = uploadedNotice;
    setUploadedNotice(null);
    if (!notice) return;

    // Within one location, PDF links are account-blind, so one upload can satisfy a second row
    // sharing the invoice number. Drop every LOADED row for that apply_to — the loaded rows are
    // always a single location's, so this never reaches the other location's list.
    removedApplyTosRef.current.add(notice.applyTo);
    const next = dropRemoved(invoices, removedApplyTosRef.current);
    setInvoices(next);
    // Do not invalidate this page's key (a refetch can yank the row while the dialog is still
    // open). Patch the cache instead so remount / window-focus does not resurrect filled rows.
    writeMissingInvoicesCache(queryClient, location, next);

    void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
    void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
  };

  const departmentOptions = useMemo(() => {
    const named = new Set<string>();
    let hasNone = false;
    invoices.forEach((row) => {
      if (row.department) named.add(row.department);
      else hasNone = true;
    });
    const sorted = Array.from(named).sort((a, b) => a.localeCompare(b));
    return hasNone ? [...sorted, NO_DEPARTMENT] : sorted;
  }, [invoices]);

  useEffect(() => {
    if (departmentFilter !== 'all' && !departmentOptions.includes(departmentFilter)) {
      setDepartmentFilter('all');
    }
  }, [departmentFilter, departmentOptions]);

  const filtered = useMemo(
    () => filterRows(invoices, companyFilter, billCodeFilter, invoiceNumberFilter, departmentFilter),
    [invoices, companyFilter, billCodeFilter, invoiceNumberFilter, departmentFilter],
  );

  const columns = useMemo(() => buildColumns(triggerUpload, uploadingApplyTo), [triggerUpload, uploadingApplyTo]);

  const handleClearFilters = () => {
    setCompanyFilter('');
    setBillCodeFilter('');
    setInvoiceNumberFilter('');
    setDepartmentFilter('all');
  };

  const tableData = isError ? [] : filtered;

  return (
    <Grid container>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      <Grid item xs={12}>
        <PageHeader
          mainTitle="Missing Invoices"
          subTitle={
            isLoading || isError
              ? undefined
              : `${filtered.length} of ${invoices.length} open ${invoices.length === 1 ? 'invoice' : 'invoices'} without a PDF`
          }
          titleIcon={<ReceiptLongIcon />}
        />
      </Grid>
      {isError && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="error">Failed to load missing invoices.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <TextField
              label="Client Name"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Bill Code"
              value={billCodeFilter}
              onChange={(e) => setBillCodeFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Invoice #"
              value={invoiceNumberFilter}
              onChange={(e) => setInvoiceNumberFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="missing-invoices-filter-department">Department</InputLabel>
              <Select
                labelId="missing-invoices-filter-department"
                label="Department"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {departmentOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ mb: 0.25 }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <Card>
          <DataTable
            columns={columns}
            data={tableData}
            keyField="composite_key"
            progressPending={isLoading}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            defaultSortField="due_date"
            defaultSortAsc
            highlightOnHover
            striped
            noHeader
            noDataComponent={isError ? '' : EMPTY_QUEUE_COPY}
          />
        </Card>
      </Grid>
      <UploadedInvoiceDialog notice={uploadedNotice} onDismiss={handleNoticeDismiss} />
    </Grid>
  );
};

export default MissingInvoicesListPage;
